import React, { useEffect } from "react";
import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { useBuyer } from "@/hooks/vb/useBuyers";
import { useReportsStore } from "@/store/store";
import "@/styles/vb.css";

/** The single mount point that scopes the aits-2.0 design system. Everything
 *  under `.vb-root` gets Figtree + the VB token-driven look; nothing outside it
 *  is affected. All VB routes render inside this wrapper.
 *
 *  It also publishes the "active company" to the shared store so the sidebar
 *  company dropdown highlights the right company while you're inside a VB —
 *  the buyer's company on a buyer route, or the ?company= scope on the
 *  overview / create routes. `useBuyer` here shares React Query's cache with
 *  the child page's own `useBuyer(buyerId)`, so it adds no extra request. */
export function VbLayout() {
  const params = useParams();
  const [searchParams] = useSearchParams();
  const setActiveVbCompanyId = useReportsStore((s) => s.setActiveVbCompanyId);

  const buyerId = Number(params.buyerId);
  const validBuyerId = Number.isInteger(buyerId) && buyerId > 0;
  const buyer = useBuyer(validBuyerId ? buyerId : undefined);

  const companyParam = searchParams.get("company");
  const companyFromParam =
    companyParam && /^\d+$/.test(companyParam) ? Number(companyParam) : null;
  const activeCompanyId = validBuyerId
    ? buyer.data?.company.id ?? null
    : companyFromParam;

  // Update on change; clear only when actually leaving the VB section (the
  // empty-dep cleanup runs on unmount), so intra-VB navigation doesn't blink
  // the highlight off and back on.
  useEffect(() => {
    setActiveVbCompanyId(activeCompanyId);
  }, [activeCompanyId, setActiveVbCompanyId]);
  useEffect(
    () => () => setActiveVbCompanyId(null),
    [setActiveVbCompanyId],
  );

  return (
    <div className="vb-root min-h-full rounded-vb-lg p-4 sm:p-6">
      <Outlet />
    </div>
  );
}

export default VbLayout;
